import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { BedDouble, LogOut, Users, Calendar } from "lucide-react";
import { toast } from "sonner";
import { CATEGORY_LABEL, label } from "@/lib/labels";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const AGE_BRACKETS = ["0-3 ans", "4-12 ans", "13-18 ans", "19-35 ans", "36-60 ans", "60+ ans"];

export default function Hospitalizations() {
  const [data, setData] = useState({ items: [], total: 0, by_age_bracket: {} });
  const load = async () => {
    const r = await axios.get(`${API}/hospitalizations`);
    setData(r.data);
  };
  useEffect(() => { load(); }, []);

  const discharge = async (p) => {
    if (!window.confirm(`Décharger ${p.name} ?`)) return;
    try {
      await axios.post(`${API}/patients/${p.id}/discharge`);
      toast.success("Patient déchargé");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const chartData = AGE_BRACKETS.map((b) => ({ tranche: b, Patients: data.by_age_bracket[b] || 0 }));
  const fmt = (d) => d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—";

  return (
    <div className="space-y-6" data-testid="hospitalizations-page">
      <div>
        <h1 className="text-3xl font-[Outfit] font-bold inline-flex items-center gap-3">
          <BedDouble className="w-7 h-7 text-[#D96C4A]"/> Patients hospitalisés
        </h1>
        <p className="text-[#5C6B61] mt-1 text-sm">Liste des patients actuellement retenus à l'hôpital · {data.total} total</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="bg-white rounded-2xl border border-[#E5EBE7] p-5">
          <div className="text-xs text-[#5C6B61] uppercase">Total hospitalisés</div>
          <div className="text-4xl font-[Outfit] font-bold mt-2 text-[#D96C4A]">{data.total}</div>
        </div>
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E5EBE7] p-5">
          <h3 className="font-[Outfit] font-semibold mb-3">Répartition par tranche d'âge</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5EBE7"/>
                <XAxis dataKey="tranche" tick={{ fontSize: 11, fill: "#5C6B61" }}/>
                <YAxis tick={{ fontSize: 11, fill: "#5C6B61" }} allowDecimals={false}/>
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5EBE7", fontSize: 12 }}/>
                <Bar dataKey="Patients" fill="#D96C4A" radius={[6, 6, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#E5EBE7] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FDFBF7] text-left text-xs uppercase text-[#5C6B61] tracking-wider">
            <tr>
              <th className="px-5 py-3">Patient</th>
              <th className="px-5 py-3">Âge</th>
              <th className="px-5 py-3">Catégorie</th>
              <th className="px-5 py-3">Chambre</th>
              <th className="px-5 py-3">Motif</th>
              <th className="px-5 py-3">Admis le</th>
              <th className="px-5 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-[#5C6B61]" data-testid="empty-hosp">Aucun patient hospitalisé actuellement</td></tr>
            ) : data.items.map((p) => (
              <tr key={p.id} className="border-t border-[#E5EBE7] hover:bg-[#FDFBF7]/50" data-testid={`hosp-row-${p.id}`}>
                <td className="px-5 py-3"><div className="font-medium">{p.name}</div><div className="text-xs text-[#5C6B61]">{p.phone}</div></td>
                <td className="px-5 py-3">{p.age}</td>
                <td className="px-5 py-3 text-xs"><span className="px-2 py-0.5 rounded-full bg-[#1E4631]/10 text-[#1E4631]">{label(CATEGORY_LABEL, p.category)}</span></td>
                <td className="px-5 py-3 font-mono">{p.room_number || "—"}</td>
                <td className="px-5 py-3 text-[#5C6B61] text-xs max-w-[250px]">{p.admission_reason}</td>
                <td className="px-5 py-3 text-xs text-[#5C6B61]">{fmt(p.admission_date)}</td>
                <td className="px-5 py-3">
                  <Button size="sm" variant="outline" onClick={()=>discharge(p)} className="text-[#2E7D32] border-[#2E7D32]/30 hover:bg-[#2E7D32]/10" data-testid={`discharge-${p.id}`}>
                    <LogOut className="w-3.5 h-3.5 mr-1"/> Décharger
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
